import React, { useCallback, useEffect, useState } from 'react'
import { CButton, CCard, CCardBody, CCol, CFormInput, CInputGroup, CRow } from '@coreui/react-pro'
import CIcon from '@coreui/icons-react'
import { cilBullhorn, cilPlus, cilSearch } from '@coreui/icons'
import Table from './Table'
import AddForm from './AddForm'
import EditForm from './EditForm'
import AdminAnnouncementService from 'src/service/AdminAnnouncementService'
import './AdminAnnouncement.css'

const AdminAnnouncement = () => {
  const [announcements, setAnnouncements] = useState([])
  const [activePage, setActivePage] = useState(1)
  const [itemsPerPage, setItemsPerPage] = useState(10)
  const [columnFilter, setColumnFilter] = useState({})
  const [columnSorter, setColumnSorter] = useState({})
  const [searchInput, setSearchInput] = useState('')
  const [addVisible, setAddVisible] = useState(false)
  const [editAnnouncementId, setEditAnnouncementId] = useState(null)
  const [loading, setLoading] = useState(false)

  const fetchAnnouncements = useCallback(async () => {
    const offset = itemsPerPage * activePage - itemsPerPage
    let params = new URLSearchParams()
    Object.keys(columnFilter).forEach((key) => {
      if (columnFilter[key]) params.append(key, columnFilter[key])
    })
    columnSorter &&
      columnSorter.column !== undefined &&
      params.append('sort', `${columnSorter.column}%${columnSorter.state}`)

    setLoading(true)
    try {
      const result = await AdminAnnouncementService.getAnnouncements(
        offset,
        itemsPerPage,
        activePage,
        params,
      )
      setAnnouncements(result.data)
    } catch (error) {
      console.error('Error fetching announcements:', error)
    } finally {
      setLoading(false)
    }
  }, [activePage, itemsPerPage, columnFilter, columnSorter])

  useEffect(() => {
    fetchAnnouncements()
  }, [fetchAnnouncements])

  // The listing searches payload->title through the `title` param.
  useEffect(() => {
    const timer = setTimeout(() => {
      const term = searchInput.trim()
      if ((columnFilter.title || '') === term) return
      setActivePage(1)
      setColumnFilter(term ? { title: term } : {})
    }, 350)
    return () => clearTimeout(timer)
  }, [searchInput, columnFilter])

  const total = announcements?.total ?? 0

  return (
    <>
      <div className="d-flex flex-wrap justify-content-between align-items-start gap-2 mb-3">
        <div className="ge-page-header mb-0">
          <h1 className="ge-page-title">Announcements</h1>
          <p className="ge-page-subtitle">
            Post updates and news to the feed your members see in the app.
          </p>
        </div>
        <CButton color="primary" onClick={() => setAddVisible(true)}>
          <CIcon icon={cilPlus} className="me-1" />
          New Announcement
        </CButton>
      </div>

      <CRow className="g-3 mb-3">
        <CCol sm={6} lg={4}>
          <CCard className="an-stat-card h-100">
            <CCardBody className="d-flex align-items-center gap-3">
              <span className="an-stat-icon">
                <CIcon icon={cilBullhorn} size="lg" />
              </span>
              <div>
                <div className="an-stat-label">Total Announcements</div>
                <div className="an-stat-value">{total}</div>
              </div>
            </CCardBody>
          </CCard>
        </CCol>
      </CRow>

      <CCard className="an-panel">
        <CCardBody>
          <div className="an-panel-toolbar">
            <div>
              <h2 className="an-panel-title">Manage Announcements</h2>
              <p className="an-panel-note">Edit, activate or remove what members see.</p>
            </div>
            <CInputGroup className="ge-search">
              <span className="ge-search-icon">
                <CIcon icon={cilSearch} />
              </span>
              <CFormInput
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                placeholder="Search by title..."
                aria-label="Search announcements by title"
              />
            </CInputGroup>
          </div>

          <Table
            announcements={announcements}
            fetchAnnouncements={fetchAnnouncements}
            listLoading={loading}
            setActivePage={setActivePage}
            setItemsPerPage={setItemsPerPage}
            setColumnSorter={setColumnSorter}
            itemsPerPage={itemsPerPage}
            activePage={activePage}
            onEditAnnouncement={setEditAnnouncementId}
          />
        </CCardBody>
      </CCard>

      <AddForm
        visible={addVisible}
        onClose={() => setAddVisible(false)}
        fetchAnnouncements={fetchAnnouncements}
      />

      <EditForm
        announcementId={editAnnouncementId}
        visible={!!editAnnouncementId}
        onClose={() => setEditAnnouncementId(null)}
        fetchAnnouncements={fetchAnnouncements}
      />
    </>
  )
}

export default AdminAnnouncement
